import { TintMark } from "@/components/TintMark/TintMark"
import { cn } from "@/lib/utils"

type PageLoaderVariant = "page" | "section" | "inline"

interface PageLoaderProps {
  /**
   * Where the loader lives, which decides how much height it may claim:
   *
   * - `page`    the route-level fallback that renders INSTEAD of the app shell,
   *             so it owns the viewport and centres in it.
   * - `section` a page that has already drawn its header and is filling the
   *             body; it centres in the space below, never the whole viewport.
   * - `inline`  a panel or dialog, where a tall box would push the chrome off
   *             screen.
   *
   * Height comes from the variant, never from measuring the DOM: a JS-measured
   * box reads its own offset before the surrounding layout settles and lands
   * the mark off-centre.
   */
  variant?: PageLoaderVariant
  /** Announced to assistive tech; visually hidden. */
  label?: string
  className?: string
}

// `page` is pinned to the viewport, not laid out in flow: any ancestor padding
// or preceding sibling would otherwise shift a flow-positioned box off centre,
// which is exactly how the mark ended up below the middle of a blank screen.
const VARIANT_BOX: Record<PageLoaderVariant, string> = {
  page: "fixed inset-0 z-50 bg-background",
  section: "min-h-[60svh]",
  inline: "min-h-48",
}

/**
 * The one loading state for whole pages and page sections: the Tint mark over a
 * slim indeterminate track. The track matters — a lone pulsing logo reads as a
 * frozen page, a moving bar reads as work in progress.
 */
const PageLoader = ({ variant = "page", label = "Loading…", className }: PageLoaderProps) => (
  <div
    className={cn("grid w-full place-items-center", VARIANT_BOX[variant], className)}
    role="status"
    aria-live="polite"
    data-testid="page-loader"
    data-variant={variant}
  >
    <div className="flex flex-col items-center gap-4">
      <TintMark className="size-10 rounded-xl motion-safe:animate-pulse" />
      <span className="tint-loader-track" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </div>
  </div>
)

export default PageLoader
